import React, { useEffect, useMemo, useState } from "react";
import { FaCheck, FaPaperPlane, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";

import {
  getVoteById,
  submitVote,
} from "../../../../../services/voteServices";

import {
  getQuestionTypeLabel,
  normalizeQuestionType,
  validateVoteResponses,
} from "../utils/userVoteUtils";

export default function ParticipateVoteDrawer({
  isOpen,
  voteId,
  userId,
  onClose,
  onSubmitted,
}) {
  const [vote, setVote] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !voteId) return;

    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setResponses({});

        const data = await getVoteById(voteId);

        if (!alive) return;

        setVote(data || null);
      } catch (error) {
        console.error("Erreur chargement vote :", error);
        toast.error("Erreur lors du chargement du vote.");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();

    return () => {
      alive = false;
    };
  }, [isOpen, voteId]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  const validation = useMemo(
    () => validateVoteResponses(vote, responses),
    [vote, responses]
  );

  const handleChange = (questionId, value, type) => {
    const normalizedType = normalizeQuestionType(type);

    if (normalizedType === "multiple") {
      setResponses((previous) => {
        const previousChoices = Array.isArray(previous[questionId])
          ? previous[questionId]
          : [];

        const nextChoices = previousChoices.includes(value)
          ? previousChoices.filter((item) => item !== value)
          : [...previousChoices, value];

        return {
          ...previous,
          [questionId]: nextChoices,
        };
      });

      return;
    }

    setResponses((previous) => ({
      ...previous,
      [questionId]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!voteId || !userId) {
      toast.error("Utilisateur non identifié.");
      return;
    }

    if (!validation.valid) {
      toast.error(
        `Veuillez répondre à toutes les questions obligatoires (${validation.missing.length} restante(s)).`
      );
      return;
    }

    try {
      setSubmitting(true);

      const ok = await submitVote({
        vote_id: voteId,
        user_id: userId,
        responses,
      });

      if (!ok) {
        throw new Error("Échec soumission vote.");
      }

      toast.success("Réponses enregistrées.");
      await onSubmitted?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur soumission vote :", error);
      toast.error("Erreur lors de l’envoi des réponses.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-4xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Participation
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white">
              {vote?.title || "Vote / sondage"}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="h-32 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
                />
              ))}
            </div>
          ) : !vote ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400">
              Impossible de charger ce vote.
            </div>
          ) : (
            <div className="space-y-5">
              {vote?.description && (
                <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
                  {vote.description}
                </div>
              )}

              {(vote.questions || []).map((question, index) => {
                const type = normalizeQuestionType(question.type);

                return (
                  <QuestionBlock
                    key={question.id}
                    index={index}
                    question={question}
                    type={type}
                    value={responses[question.id]}
                    onChange={handleChange}
                  />
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white/95 p-3 dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-xs font-bold text-slate-500 dark:text-slate-400">
              {validation.valid
                ? "Toutes les questions obligatoires sont complétées."
                : `${validation.missing.length} question(s) obligatoire(s) restante(s).`}
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || loading || !vote}
                className="inline-flex items-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaPaperPlane />
                {submitting ? "Envoi..." : "Envoyer"}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function QuestionBlock({ index, question, type, value, onChange }) {
  return (
    <article className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-bold text-slate-900 dark:text-white">
            Question {index + 1}
          </p>

          <span className="rounded-full bg-indigo-50 px-3 py-1 text-[11px] font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-200 dark:ring-indigo-800/60">
            {getQuestionTypeLabel(type)}
          </span>
        </div>
      </div>

      <div className="p-4">
        <div className="prose prose-sm max-w-none dark:prose-invert">
          <ReactQuill value={question.question_text || ""} readOnly theme="bubble" />
        </div>

        {type === "open" && (
          <textarea
            rows={4}
            value={value || ""}
            onChange={(event) =>
              onChange(question.id, event.target.value, question.type)
            }
            placeholder="Votre réponse..."
            className="mt-3 w-full rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-900/30"
          />
        )}

        {type === "single" && (
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {(question.choices || []).map((choice) => {
              const checked = value === choice.id;

              return (
                <button
                  key={choice.id}
                  type="button"
                  onClick={() => onChange(question.id, choice.id, question.type)}
                  className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left text-sm font-bold transition ${
                    checked
                      ? "border-indigo-300 bg-indigo-50 text-indigo-700 ring-2 ring-indigo-100 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-200 dark:ring-indigo-500/20"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                  }`}
                >
                  <span>{choice.choice_text}</span>

                  {checked && (
                    <span className="grid h-7 w-7 place-items-center rounded-xl bg-indigo-600 text-white">
                      <FaCheck />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {type === "multiple" && (
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {(question.choices || []).map((choice) => {
              const checked = Array.isArray(value) && value.includes(choice.id);

              return (
                <button
                  key={choice.id}
                  type="button"
                  onClick={() => onChange(question.id, choice.id, question.type)}
                  className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left text-sm font-bold transition ${
                    checked
                      ? "border-emerald-300 bg-emerald-50 text-emerald-700 ring-2 ring-emerald-100 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200 dark:ring-emerald-500/20"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                  }`}
                >
                  <span>{choice.choice_text}</span>

                  {checked && (
                    <span className="grid h-7 w-7 place-items-center rounded-xl bg-emerald-600 text-white">
                      <FaCheck />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </article>
  );
}